'use client';

import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, RefreshCw, Check, Mail } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomEmailGeneratorContent } from '../../data/random-email-generator-content';
import Link from 'next/link';

const DOMAINS = [
    'example.com',
    'test.com',
    'mail.com',
    'email.com',
    'mail.me',
    'test.org',
    'example.org',
    'temporary.com',
    'disposable.com',
    'fakeinbox.com',
];

const USERNAME_LENGTH = 8; // Default length for random usernames

function generateRandomString(length: number): string {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
}

function generateRandomEmail(usernameLength: number, domain: string): string {
    const username = generateRandomString(usernameLength);
    return `${username}@${domain}`;
}

export default function RandomEmailGenerator() {
    const [email, setEmail] = useState('');
    const [usernameLength, setUsernameLength] = useState(USERNAME_LENGTH);
    const [customDomain, setCustomDomain] = useState('');
    const [copied, setCopied] = useState(false);
    const { toast } = useToast();

    const generateEmail = useCallback(() => {
        const domain = customDomain || DOMAINS[Math.floor(Math.random() * DOMAINS.length)];
        const newEmail = generateRandomEmail(usernameLength, domain);
        setEmail(newEmail);
    }, [usernameLength, customDomain]);

    const copyToClipboard = useCallback((text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            toast({
                title: 'Copied!',
                description: 'Email address has been copied to clipboard.',
            });
            setTimeout(() => setCopied(false), 2000);
        });
    }, [toast]);

    // Generate an email when the component mounts
    useState(() => {
        generateEmail();
    });

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-2xl mx-auto">
                <ToolPageIntro title="Random Email Generator">
                Generate random email addresses with our free online tool. Create realistic email addresses for testing, validation, and privacy protection.
            </ToolPageIntro>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Generated Email</CardTitle>
                            <CardDescription>Your randomly generated email address</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center space-x-2">
                                <div className="relative flex-1">
                                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        className="pl-10 text-lg font-mono"
                                        value={email}
                                        readOnly
                                        onClick={(e) => (e.target as HTMLInputElement).select()}
                                    />
                                </div>
                                <Button
                                    size="icon"
                                    variant="outline"
                                    onClick={() => copyToClipboard(email)}
                                    disabled={!email}
                                >
                                    {copied ? (
                                        <Check className="h-5 w-5 text-green-500" />
                                    ) : (
                                        <Copy className="h-5 w-5" />
                                    )}
                                </Button>
                                <Button onClick={generateEmail}>
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Generate
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Customization</CardTitle>
                            <CardDescription>Customize how emails are generated</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="usernameLength">Username Length: {usernameLength}</Label>
                                <Input
                                    id="usernameLength"
                                    type="range"
                                    min="4"
                                    max="32"
                                    value={usernameLength}
                                    onChange={(e) => setUsernameLength(Number(e.target.value))}
                                    className="w-full"
                                />
                                <div className="flex justify-between text-sm text-muted-foreground">
                                    <span>4 chars</span>
                                    <span>32 chars</span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="customDomain">Custom Domain (optional)</Label>
                                <div className="flex space-x-2">
                                    <Input
                                        id="customDomain"
                                        type="text"
                                        placeholder="example.com"
                                        value={customDomain}
                                        onChange={(e) => setCustomDomain(e.target.value)}
                                        className="flex-1"
                                    />
                                    {customDomain && (
                                        <Button
                                            variant="outline"
                                            onClick={() => setCustomDomain('')}
                                        >
                                            Reset
                                        </Button>
                                    )}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    Leave empty to use a random domain
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label>Common Domains</Label>
                                <div className="flex flex-wrap gap-2">
                                    {DOMAINS.map((domain) => (
                                        <Button
                                            key={domain}
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCustomDomain(domain)}
                                            className={customDomain === domain ? 'bg-primary/10' : ''}
                                        >
                                            @{domain}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>How to Use</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <h3 className="font-medium">1. Generate Emails</h3>
                                <p className="text-sm text-muted-foreground">
                                    Click the "Generate" button to create a new random email address. The email will be copied to your clipboard automatically.
                                </p>
                            </div>
                            <div className="space-y-2">
                                <h3 className="font-medium">2. Customize</h3>
                                <p className="text-sm text-muted-foreground">
                                    Adjust the username length or select a specific domain from the list. You can also enter a custom domain.
                                </p>
                            </div>
                            <div className="space-y-2">
                                <h3 className="font-medium">3. Copy & Use</h3>
                                <p className="text-sm text-muted-foreground">
                                    Click on the email to select it, or use the copy button. These are not real email addresses and cannot receive emails.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomEmailGeneratorContent.title}
                description={randomEmailGeneratorContent.description}
                content={randomEmailGeneratorContent.content}
                faqs={randomEmailGeneratorContent.faqs}
                relatedTools={randomEmailGeneratorContent.relatedTools}
            />

            <Toaster />
        </div>
    );
}
